import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import useModal from '@/common/hooks/useModal';
import useSignature from '@/common/hooks/useSignature';
import { getSignatureQueryOptions } from '@/modules/adjusterReportModules/queries/getSignature/getSignatureQueryOptions';
import { postSignatureQueryOptions } from '@/modules/adjusterReportModules/queries/postSignature/postSignatureQueryOptions';
import { patchSignatureQueryOptions } from '@/modules/adjusterReportModules/queries/patchSignature/patchSignatureQueryOptions';
import { FileEnType } from '@/modules/adjusterReportModules/types/signatureQueryParams.type';
import { useNavigate } from 'react-router-dom';
import { ConvertUrlToFile } from '@/common/utils/ConvertUrlToFile';

type UseSignatureLogicProps = {
  id: string;
  damagedId: string;
  editable: boolean;
  type: FileEnType;
};

export const useExpertSignature = ({
  id,
  damagedId,
  editable,
  type = 'Initial',
}: UseSignatureLogicProps) => {
  const signature = useSignature();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const {
    closeModal: confirmationCloseModal,
    open: confirmationOpen,
    openModal: confirmationOpenModal,
  } = useModal();

  const generateFormData = (blob: Blob) => {
    const formData = new FormData();
    const image = ConvertUrlToFile(blob, 'image', 'image');

    formData.append('image', image);
    return formData;
  };

  const { data: signatureImage, isPending: getSignatureLoading } = useQuery(
    getSignatureQueryOptions({ damagedId, id }, type),
  );

  const { mutate: postMutate, isPending: postLoading } = useMutation(
    postSignatureQueryOptions(type),
  );
  const { mutate: patchMutate, isPending: patchLoading } = useMutation(
    patchSignatureQueryOptions(type),
  );

  const postSignatureOnSuccess = () => {
    confirmationCloseModal();
    queryClient.invalidateQueries({
      queryKey: getSignatureQueryOptions(
        {
          damagedId,
          id,
        },
        type,
      ).queryKey,
    });
    navigate(-1);
  };

  const submitSignature = async () => {
    const signBlob = await signature.getImage();
    const formData = generateFormData(signBlob);
    if (!signatureImage?.image) {
      postMutate(
        { damagedId, id, data: formData },
        {
          onSuccess: postSignatureOnSuccess,
        },
      );
    } else {
      patchMutate(
        { damagedId, id, data: formData },
        {
          onSuccess: postSignatureOnSuccess,
        },
      );
    }
  };

  return {
    isLoading: getSignatureLoading,
    signatureImage: signatureImage?.image,
    signatureProps: signature,
    onOpenConfirmation: confirmationOpenModal,
    confirmationOpen,
    onCloseConfirmation: confirmationCloseModal,
    onSubmitSignature: submitSignature,
    postLoading,
    patchLoading,
    editable,
  };
};
